import { SocialPlatform } from '@mg/shared';
import { cn } from '../../lib/cn';
import { PostText, PreviewActionRow, PreviewAvatar } from './PlatformPreview';
import { PLATFORM_META } from './platforms';
import { PreviewImageGrid } from './PreviewImageGrid';
import { PREVIEW_STYLE, previewIdentity, type PreviewAccount } from './previewRules';
import type { ThreadPreviewPost } from './threadPreviewPosts';

export interface ThreadPreviewProps {
  /** The two platforms that take a thread. */
  platform: SocialPlatform.X | SocialPlatform.THREADS;
  /** The connected account the thread goes out as. */
  account: PreviewAccount;
  /** The thread's posts in the order they publish, as `threadPreviewPosts` works them out. */
  posts: ThreadPreviewPost[];
  /**
   * The workspace's logo, as an image URL ready for an <img> (already past the auth proxy): drawn in
   * every post's avatar circle. Null or absent: the account's initials, as before.
   */
  avatarUrl?: string | null;
}

/** Where a post has neither text nor media - in muted words, since they are not the post's. */
const UNTITLED_POST = 'Untitled post';

/**
 * A thread as it will look in the platform's feed: ONE card, its posts stacked in the order they
 * publish, the account's picture beside each and a line running down from one picture to the next.
 * Each post shows who it is from, its text, its pictures and the platform's action row - a mirror
 * of what will be published, so nothing stands in for a text that is not there.
 *
 * No state and nothing to press: X and Threads show a post whole.
 */
export function ThreadPreview({ platform, account, posts, avatarUrl = null }: ThreadPreviewProps) {
  const meta = PLATFORM_META[platform];
  const { timeLabel } = PREVIEW_STYLE[platform];
  // One account for the whole thread, so who a post is from is worked out once.
  const identity = previewIdentity(platform, account);
  const isX = platform === SocialPlatform.X;
  return (
    <article
      aria-label={`${meta.label} thread preview`}
      // The single card's shell (PlatformPreview): the same width, border and corners, and light in
      // both themes like that card (theme-light; no dark: class anywhere inside it).
      className="theme-light mx-auto w-full max-w-[25rem] overflow-hidden rounded-lg border border-line bg-card text-left [container-type:inline-size]"
    >
      {/* role="list": the order is the content, and Safari drops the role with the markers. */}
      <ol role="list" className="px-3 pt-3">
        {posts.map((post, i) => (
          <li key={post.key} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5">
            {/* The rail is as tall as its post: the line fills it from under this avatar down to
                the next one. Any space between two posts therefore belongs to the content column,
                never to the <li> - there it would break the line. */}
            <div className="flex flex-col items-center">
              {/* The avatar of the single card, at that card's size for the platform: the
                  workspace's logo when there is one, else the account's initials. */}
              <PreviewAvatar name={identity.name} size={isX ? 'md' : 'sm'} avatarUrl={avatarUrl} />
              {i < posts.length - 1 && (
                // A border, not a background: forced colours keeps a border.
                <span aria-hidden="true" className="my-1 w-0 flex-1 border-l-2 border-line-dashed" />
              )}
            </div>
            {/* A size container of its own: the action row reads THIS column's width, which beside
                the avatar is a good deal narrower than the card. */}
            <div className="min-w-0 [container-type:inline-size]">
              <p className="flex min-w-0 items-baseline gap-1 pb-0.5 text-sm leading-tight">
                <span className="truncate font-semibold text-ink">{identity.name}</span>
                {identity.handle && (
                  <span className="truncate text-ink-muted">{identity.handle}</span>
                )}
                <span className="shrink-0 text-ink-muted">
                  {isX && <span aria-hidden="true">· </span>}
                  {timeLabel}
                </span>
              </p>
              {post.empty ? (
                <p className="pb-2 text-sm text-ink-muted">{UNTITLED_POST}</p>
              ) : post.text ? (
                <p className="whitespace-pre-wrap break-words pb-2 text-sm text-ink">
                  <PostText text={post.text} linkClass={meta.link} />
                </p>
              ) : null}
              {post.hashtags.length > 0 && (
                <p className={cn('break-words pb-2 text-sm', meta.link)}>
                  {post.hashtags.join(' ')}
                </p>
              )}
              {/* The grid the single post's preview draws too (PreviewImageGrid), in this card's
                  frame. */}
              <PreviewImageGrid
                pictures={post.pictures}
                more={post.more}
                className="mt-1 overflow-hidden rounded-lg border border-line"
              />
              <PreviewActionRow platform={platform} inset />
            </div>
          </li>
        ))}
      </ol>
    </article>
  );
}
